@if (isLoading$ | async) {
    <valumize-loading-spinner></valumize-loading-spinner>
}
<mat-sidenav-container>
    <mat-sidenav #sidenav class="mat-elevation-z8" mode="side" opened>
        @if (sidenav.opened) {
            <div class="top-right-nav-toggle">
                <button (click)="sidenav.toggle()" color="primary" mat-icon-button>
                    <mat-icon>close</mat-icon>
                </button>
            </div>
        }
        <div>
            <div class="sidenav-info">
                <div>
                    @if (closingDate$ | async; as closingDate) {
                        <div>
                            <label data-test="closing-date-label">Closing Date</label>
                            <p data-test="closing-date">{{ closingDate | traceableFormat }}</p>
                        </div>
                    }
                    @if (selectedSeller$ | async; as seller) {
                        @if (!!seller.id) {
                            <div style="display: flex">
                                <div>
                                    <label data-test="seller-label">Seller</label>
                                    <p data-test="seller">{{ seller.name | traceableFormat }}</p>
                                </div>
                                <div>
                                    <button
                                        (click)="openNewSellerDialog()"
                                        color="primary"
                                        data-test="edit-seller-button"
                                        mat-icon-button>
                                        <mat-icon>manage_history</mat-icon>
                                    </button>
                                </div>
                            </div>
                        } @else {
                            <button (click)="openNewSellerDialog()"
                                    color="primary"
                                    data-test="create-or-select-seller"
                                    mat-stroked-button>
                                <mat-icon>add</mat-icon>
                                Seller
                            </button>
                        }
                    }
                </div>
            </div>
            <mat-divider></mat-divider>
            <mat-action-list>
                <a (click)="scrollToComponent('deal-details')" data-test="deal-details-nav-item">
                    <mat-list-item>Deal Details</mat-list-item>
                </a>
                <a (click)="scrollToComponent('deal-funds')" data-test="deal-funds-nav-item">
                    <mat-list-item>Funds</mat-list-item>
                </a>
                <a (click)="scrollToComponent('deal-companies')" data-test="deal-companies-nav-item">
                    <mat-list-item>Companies</mat-list-item>
                </a>
                <a (click)="scrollToComponent('return-summary')" data-test="return-summary-nav-item">
                    <mat-list-item>Return Summary</mat-list-item>
                </a>
                <a (click)="scrollToComponent('deal-cashflows-graph')" data-test="deal-cashflows-graph-nav-item">
                    <mat-list-item>Deal Cashflows Graph</mat-list-item>
                </a>
                <a (click)="scrollToComponent('deal-cashflows')" data-test="deal-cashflows-nav-item">
                    <mat-list-item>Deal Cashflows</mat-list-item>
                </a>
                <a (click)="scrollToComponent('documents')" data-test="documents-nav-item">
                    <mat-list-item>Documents</mat-list-item>
                </a>
            </mat-action-list>
        </div>
    </mat-sidenav>
    <mat-sidenav-content>
        @if (!sidenav.opened) {
            <div class="top-left-nav-toggle">
                <button (click)="sidenav.toggle()" color="primary" mat-icon-button>
                    <mat-icon>menu</mat-icon>
                </button>
            </div>
        }

        @let dealName = (dealName$ | async);
        @let dealArchived = (isArchived$ | async);
        <div class="breadcrumb-container">
            @if (dealName) {
                <label class="breadcrumb" data-test="breadcrumb">
                    <mat-icon>work_history</mat-icon>
                    {{ dealName.name | traceableFormat }}
                </label>
            }
            <div class="detail-page-top-right-menu">
                <button (click)="onDeleteButtonClick($event)" [matMenuTriggerFor]="menu" aria-label="More options" mat-icon-button>
                    <mat-icon>more_vert</mat-icon>
                </button>
                <mat-menu #menu="matMenu">
                    <button (click)="openCopyDialog()" data-test="copy-deal-menu-item" mat-menu-item>
                        <mat-icon>content_copy</mat-icon>
                        <span>Copy Deal</span>
                    </button>
                    <button mat-menu-item data-test="sensitivity-analysis-menu-item" [routerLink]="['/deals', dealId, 'sensitivity']">
                        <mat-icon>insights</mat-icon>
                        <span>Sensitivity Analysis</span>
                    </button>
                    <button mat-menu-item data-test="generate-document-menu-item" (click)="openDocumentGeneratorDialog()">
                        <mat-icon>article_shortcut</mat-icon>
                        <span>Generate Document</span>
                    </button>
                    @if (showDeleteButton) {
                        <button (click)="openDeleteConfirmation()" data-test="delete-deal-menu-item" mat-menu-item>
                            <mat-icon>delete</mat-icon>
                            <span>Delete Deal</span>
                        </button>
                    }
                    <button mat-menu-item (click)="openArchiveConfirmation(!dealArchived?.value)">
                        <mat-icon>{{ dealArchived?.value ? 'unarchive' : 'archive' }}</mat-icon>
                        <span>{{ dealArchived?.value ? 'Unarchive' : 'Archive' }}</span>
                    </button>
                </mat-menu>
            </div>
        </div>
        <div class="page-content">
            <form [formGroup]="dealNameForm">
                <div class="detail-page-header">
                    <div>
                        @if (!dealNameIsEditable && dealName) {
                            <h1 data-test="deal-name">{{ dealName.name | traceableFormat }}</h1>
                        }
                        @if (dealNameIsEditable) {
                            <mat-form-field appearance="fill">
                                <input data-test="deal-name-input"
                                       formControlName="dealName"
                                       matInput
                                       placeholder="Deal Name">
                            </mat-form-field>
                        }
                    </div>
                    <div class="name-actions">
                        @if (!dealNameIsEditable) {
                            <button (click)="editMode()" [disabled]="(isEditDisabled$ | async)!" color="primary" data-test="edit-button"
                                    mat-icon-button>
                                <mat-icon>edit</mat-icon>
                            </button>
                        }
                        @if (dealNameIsEditable) {
                            <button (click)="save()" [disabled]="dealNameForm.invalid" color="primary" data-test="save-button" mat-icon-button>
                                <mat-icon>save</mat-icon>
                            </button>
                        }
                        @if (dealNameIsEditable) {
                            <button (click)="cancel()" color="primary" data-test="cancel-button" mat-icon-button>
                                <mat-icon>cancel</mat-icon>
                            </button>
                        }
                        @if (dealArchived?.value) {
                            <mat-chip
                                class="archived"
                                [disableRipple]="true"
                                matTooltip="{{dealArchived?.modDate | date}}">
                                <mat-icon matChipAvatar>inventory_2</mat-icon>
                                <span>Archived</span>
                            </mat-chip>
                        }
                    </div>
                    <valumize-favorite-star
                        [favType]="'DEAL'"
                        [label]="dealName?.name?.text ?? ''"
                        [context]="dealId ? ('Deal #' + dealId) : undefined">
                    </valumize-favorite-star>
                </div>
            </form>
            @let errorMsg = (errorMsg$ | async);
            @if (errorMsg !== '') {
                <valumize-info-panel [appearance]="'ERROR'" [message]="errorMsg ?? ''"></valumize-info-panel>
            }
            <valumize-deal-details id="deal-details"></valumize-deal-details>
            <valumize-deal-funds id="deal-funds"></valumize-deal-funds>
            <valumize-deal-companies id="deal-companies"></valumize-deal-companies>
            <valumize-deal-return-summary id="return-summary"></valumize-deal-return-summary>
            <valumize-deal-cashflows-graph id="deal-cashflows-graph"></valumize-deal-cashflows-graph>
            <valumize-deal-cashflows id="deal-cashflows"></valumize-deal-cashflows>
            <valumize-document id="documents" [context]="'/portfolio/deals/' + dealId + '/'"></valumize-document>
            <br/>
            <br/>
            <br/>
        </div>
    </mat-sidenav-content>
</mat-sidenav-container>

